import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, PencilLine, Utensils, Check, X } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { useGuest } from "@/contexts/GuestContext";

/**
 * PersonalisedGreeting
 *
 * A small band that appears above the countdown when a guest arrives
 * via their personalised link (`?g=magda-k`). Content depends on state:
 *   - No RSVP yet    → warm greeting + "REPLY NOW" CTA
 *   - RSVP = yes     → "See you, <name>" + meal/preorder summary + "Modify RSVP"
 *   - RSVP = no      → gentle "sorry we'll miss you" + "Change your mind?"
 */

const COPY = {
  en: {
    hi: "Welcome back",
    firstTime: "Welcome",
    noReplyKicker: "You haven't replied yet",
    noReplyBody: "Andreas is refreshing his RSVP list. Yours would look nice on it.",
    noReplyCta: "Reply now",
    yesKicker: "You're on the list",
    yesBody: "See you at Ammos Kalambouri, 19 May 2027.",
    noKicker: "Sorry to miss you",
    noBody: "You said you can't make it. Change your mind? Andreas would understand.",
    modify: "Modify RSVP",
    reconsider: "Change my reply",
    guests: "guests",
    kids: "kids",
    meal: "Meal",
  },
  fr: {
    hi: "Content de te revoir",
    firstTime: "Bienvenue",
    noReplyKicker: "Tu n'as pas encore répondu",
    noReplyBody: "Andreas met sa liste RSVP à jour. La tienne y ferait bien.",
    noReplyCta: "Répondre",
    yesKicker: "Tu es sur la liste",
    yesBody: "À bientôt à Ammos Kalambouri, le 19 mai 2027.",
    noKicker: "Dommage",
    noBody: "Tu as dit que tu ne pouvais pas venir. Tu changes d'avis ? Andreas comprendrait.",
    modify: "Modifier ma réponse",
    reconsider: "Changer ma réponse",
    guests: "invités",
    kids: "enfants",
    meal: "Menu",
  },
  el: {
    hi: "Καλωσόρισες πάλι",
    firstTime: "Καλωσόρισες",
    noReplyKicker: "Δεν έχεις απαντήσει ακόμα",
    noReplyBody: "Ο Andreas ενημερώνει τη λίστα RSVP. Θα ήταν ωραία να είσαι μέσα.",
    noReplyCta: "Απάντησε τώρα",
    yesKicker: "Είσαι στη λίστα",
    yesBody: "Τα λέμε στον Άμμο Καλαμπούρη, 19 Μαΐου 2027.",
    noKicker: "Κρίμα",
    noBody: "Είπες ότι δεν μπορείς. Αλλάζεις γνώμη; Ο Andreas θα καταλάβει.",
    modify: "Άλλαξε την απάντηση",
    reconsider: "Αλλαγή απάντησης",
    guests: "άτομα",
    kids: "παιδιά",
    meal: "Γεύμα",
  },
  pl: {
    hi: "Miło znów cię widzieć",
    firstTime: "Witaj",
    noReplyKicker: "Nie odpowiedziałeś jeszcze",
    noReplyBody: "Andreas aktualizuje listę RSVP. Twoje imię by na niej pasowało.",
    noReplyCta: "Odpowiedz teraz",
    yesKicker: "Jesteś na liście",
    yesBody: "Do zobaczenia w Ammos Kalambouri, 19 maja 2027.",
    noKicker: "Szkoda",
    noBody: "Powiedziałeś, że nie możesz. Zmieniłeś zdanie? Andreas zrozumie.",
    modify: "Zmień odpowiedź",
    reconsider: "Zmień odpowiedź",
    guests: "osób",
    kids: "dzieci",
    meal: "Menu",
  },
};

const firstNameOf = (full) => (full || "").trim().split(/\s+/)[0] || "";

const summarisePreorder = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "";
  return items
    .map((p) => `${p.quantity}× ${p.name}${p.variant ? ` · ${p.variant}` : ""}`)
    .join(", ");
};

const PersonalisedGreeting = () => {
  const { lang, t } = useLang();
  const { guestSlug, guestRsvp } = useGuest();
  const c = COPY[lang] || COPY.en;

  // Show only when we have a magic-link slug (either fresh from URL or persisted)
  if (!guestSlug) return null;

  const first = firstNameOf(guestRsvp?.name);
  const hasReplied = !!guestRsvp;
  const attending = guestRsvp?.attending === "yes";
  const declined = guestRsvp?.attending === "no";
  const preorderText = summarisePreorder(guestRsvp?.preorder);
  const kids = Number(guestRsvp?.kid_count || 0);
  const heads = Number(guestRsvp?.guest_count || 0);

  return (
    <section
      className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-12 sm:pt-16"
      data-testid="personalised-hero"
    >
      <div className="surface-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="w-12 h-12 rounded-full bg-accent/15 text-accent inline-flex items-center justify-center shrink-0">
          {hasReplied && attending ? (
            <Check className="w-5 h-5" />
          ) : hasReplied && declined ? (
            <X className="w-5 h-5" />
          ) : (
            <Sparkles className="w-5 h-5" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="editorial-kicker text-foreground/60">
            {first ? `${c.hi}, ${first}` : c.firstTime}
          </p>

          {!hasReplied && (
            <>
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 leading-tight">
                {c.noReplyKicker}
              </h3>
              <p className="text-sm text-foreground/70 mt-2 max-w-[52ch]">{c.noReplyBody}</p>
            </>
          )}

          {hasReplied && attending && (
            <>
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 leading-tight">
                {c.yesKicker}
                {heads > 1 && <span className="text-foreground/60"> · {heads} {c.guests}</span>}
                {kids > 0 && <span className="text-foreground/60"> · {kids} {c.kids}</span>}
              </h3>
              <p className="text-sm text-foreground/70 mt-2">{c.yesBody}</p>
              {preorderText && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-foreground/[0.04] border border-border px-3 py-1.5">
                  <Utensils className="w-3.5 h-3.5 text-accent" />
                  <span className="text-[12px] text-foreground/75">
                    <span className="text-foreground/50 uppercase tracking-[0.14em] text-[10px] mr-1">{c.meal}</span>
                    {preorderText}
                  </span>
                </div>
              )}
            </>
          )}

          {hasReplied && declined && (
            <>
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 leading-tight">{c.noKicker}</h3>
              <p className="text-sm text-foreground/70 mt-2 max-w-[52ch]">{c.noBody}</p>
            </>
          )}
        </div>

        <div className="shrink-0">
          <Link
            to={`/${lang}/rsvp`}
            data-testid="personalised-hero-cta"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-foreground text-background hover:bg-foreground/90 text-[12px] tracking-[0.22em] uppercase"
          >
            {!hasReplied && (t("home.rsvpCta") || c.noReplyCta)}
            {hasReplied && attending && <><PencilLine className="w-4 h-4" />{c.modify}</>}
            {hasReplied && declined && <><PencilLine className="w-4 h-4" />{c.reconsider}</>}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PersonalisedGreeting;
